import React from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../models/api/analyticsMetricsModel';
import {
  AnalyticsReportConfig,
  MetricCategoriesEnum,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from './reportTypes';

export function getWorkloadReportConfig(): AnalyticsReportConfig {
  return {
    title: <Trans id="analytics-workload-report-title">Workload</Trans>,
    reportElements: [
      {
        /* Key Metrics. */
        id: ReportElementIdsEnum.WORKLOAD_KEY_METRICS,
        metrics: [
          {
            id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
        ],
      },
      {
        /* Workload over time. */
        id: ReportElementIdsEnum.WORKLOAD_OVER_TIME,
        metrics: [
          {
            id: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          // TODO Add ACTIVE_TEAMMATES_GRAPH - https://front.atlassian.net/browse/PB-51894
        ],
      },
      {
        /* Busiest times heatmap. */
        id: ReportElementIdsEnum.WORKLOAD_BUSIEST_TIMES,
        metrics: [{id: AnalyticsHeatMapMetricsEnum.INBOX, isDisplayedByDefault: true}],
        columns: [
          {
            id: AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.WORKFORCE,
          },
        ],
      },
      {
        /* Workload over time */
        id: ReportElementIdsEnum.WORKLOAD_EFFICIENCY,
        metrics: [
          // Scalar metrics are also included so that they are fetched for the donut at the same time as the histogram.
          {
            id: AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.EFFICIENCY,
          },

          {
            id: AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },

          {
            id: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },

          {
            id: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
        ],
      },
      {
        /* Conversation Stages. */
        id: ReportElementIdsEnum.WORKLOAD_CONVERSATION_STAGES,
        metrics: [
          {id: AnalyticsTableMetricsEnum.STAGE_PCT_RESOLVED_SEGMENTS_TABLE, isDisplayedByDefault: true},
        ],
      },
      {
        /* Workload by inbox. */
        id: ReportElementIdsEnum.WORKLOAD_BY_INBOX,
        metrics: [{id: AnalyticsTableMetricsEnum.INBOX_TABLE, isDisplayedByDefault: true}],
      },
    ],
    options: {
      [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: {
        // Default buckets for the reply time histogram metrics: < 15min, 15min - 45min, 45m - 1h, 1h - 2h, > 2h
        [AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM]: [900000, 2700000, 3600000, 7200000],
        [AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM]: [900000, 2700000, 3600000, 7200000],
        // Default buckets for the stage time histogram metric: < 1d, 1d - 2d, 2d - 4d, 4d - 7d, > 7d
        [AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO]: [
          86400000, 172800000, 345600000, 604800000,
        ],
      },
    },
    isBeta: true,
    isNew: false,
  };
}
